import { useMemo, useState } from "react";
import { walk } from "../../catalogue/types";
import { useCatalogue, useLabels, useMapCore, usePanel } from "../hooks";
import { Drawer } from "../primitives/Drawer";
import { Input } from "../primitives/Input";
import { PanelMenu } from "./PanelMenu";
import { UnifiedTreeBody } from "./SidePanel";

export type LayerDrawerProps = {
  open?: boolean;
  onClose?: (() => void) | null;
  pinnable?: boolean;
  embedded?: boolean;
  className?: string;
};

export function LayerDrawerBody({ filter }: { filter: string }) {
  return <UnifiedTreeBody filter={filter} onTable={null} />;
}

export function LayerDrawer({ open, onClose = null, pinnable = false, embedded = false, className }: LayerDrawerProps) {
  const core = useMapCore();
  const labels = useLabels();
  const catalogue = useCatalogue();
  const { panel, pinned, setPanel } = usePanel();
  const [filter, setFilter] = useState("");
  const isOpen = open ?? panel === "layers";

  const groupKeys = useMemo(() => (catalogue === null ? [] : [...walk(catalogue.maps), ...walk(catalogue.layers)].filter(a => a.kind === "group").map(a => `${a.root}:${a.key}`)), [catalogue]);
  const setAll = (collapsed: boolean) => {
    for (const key of groupKeys) core.layers.setCollapsed(key, collapsed);
  };

  const close = onClose ?? (() => setPanel(null));

  const search = <Input type="search" icon="search" value={filter} placeholder={labels.layersSearch} aria-label={labels.layersSearch} onChange={event => setFilter(event.target.value)} onClear={() => setFilter("")} clearLabel={labels.close} data-fxmap="layer-search" />;

  if (embedded) {
    return (
      <div className={["fxmap-layer-panel", className ?? ""].filter(Boolean).join(" ")} data-fxmap="layer-panel">
        <div className="fxmap-layer-panel-tools">
          {search}
          <PanelMenu onCollapseAll={() => setAll(true)} onExpandAll={() => setAll(false)} />
        </div>
        <LayerDrawerBody filter={filter} />
      </div>
    );
  }

  return (
    <Drawer
      open={isOpen}
      title={labels.layers}
      side="left"
      role="dialog"
      onClose={close}
      closeLabel={labels.close}
      pinned={pinnable ? pinned : null}
      onPin={pinnable ? value => core.store.getState().patch({ panelPinned: value }) : null}
      pinLabel={labels.pinPanel}
      unpinLabel={labels.unpinPanel}
      header={<PanelMenu onCollapseAll={() => setAll(true)} onExpandAll={() => setAll(false)} />}
      className={["fxmap-layer-panel", className ?? ""].filter(Boolean).join(" ")}
      dataFxmap="layer-panel"
    >
      <div className="fxmap-layer-panel-search">{search}</div>
      <LayerDrawerBody filter={filter} />
    </Drawer>
  );
}
